


















































import PluginComponent from '@/components/plugins/PluginComponent';
import CesiumMapService from '@/services/CesiumMapService';
import MapService from '@/services/MapService';
import { EVENTS } from '@/services/EventBus';
import { MousePosition } from 'ol/control';
import { format } from 'ol/coordinate';
import { transform } from 'ol/proj';
import sanitizeHtml from 'sanitize-html';
import Component from 'vue-class-component';

declare let Cesium: any;

@Component
export default class MousePositionPlugin extends PluginComponent {
  mousePosition!: MousePosition;

  posText = '';

  isCesium = false;

  isEditing = false;

  editX = '';

  editY = '';

  private lastCoordinates: number[] | null = null;

  mounted(): void {
  

    if (this.getMapService() instanceof MapService) {
      this.addControl();
    } else if (this.getMapService() instanceof CesiumMapService) {
      this.isCesium = true;
      this.add3DControl();
    }
  }

  beforeDestroy(): void {
    if (this.getMapService() instanceof MapService && this.getOpenLayersMapService().map !== null) {
      this.getOpenLayersMapService().map.removeControl(this.mousePosition);
    }
  }

  getDisplayProj(): string {
    return this.plugin.getParameterValue('displayProj');
  }

  getPrecision(): number {
    return this.plugin.getParameterValue('precision');
  }

  formatCoordValue(value: number): string {
    return value.toFixed(this.getPrecision());
  }

  getEditCoordinates(): number[] | null {
    if (this.lastCoordinates) {
      return [...this.lastCoordinates];
    }
    const map = this.getOpenLayersMapService().map;
    const center = map.getView().getCenter();
    if (!center) {
      return null;
    }
    return transform(
      center,
      this.getOpenLayersMapService().mapProjectionCode,
      this.getDisplayProj(),
    );
  }

  startEdit(): void {
    const coords = this.getEditCoordinates();
    if (!coords) {
      return;
    }
    this.editX = this.formatCoordValue(coords[0]);
    this.editY = this.formatCoordValue(coords[1]);
    this.isEditing = true;
    this.$nextTick(() => {
      const input = this.$refs.editXInput as HTMLInputElement | undefined;
      input?.focus();
      input?.select();
    });
  }

  cancelEdit(): void {
    this.isEditing = false;
  }

  validateEdit(): void {
    const x = parseFloat(this.editX.replace(',', '.'));
    const y = parseFloat(this.editY.replace(',', '.'));
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      this.getEventBus().$emit(EVENTS.ERROR, 'Coordonnées invalides');
      return;
    }
    const mapService = this.getOpenLayersMapService();
    const center = transform(
      [x, y],
      this.getDisplayProj(),
      mapService.mapProjectionCode,
    );
    mapService.map.getView().setCenter(center);
    this.lastCoordinates = [x, y];
    this.isEditing = false;
  }

  addControl(): void {
    const formatParam = sanitizeHtml(this.plugin.getParameterValue('format'));
    const precisionParam = this.getPrecision();
    const el: any = this.$refs.mousePosition;
    this.mousePosition = new MousePosition({
      projection: this.getDisplayProj(),
      className: 'mouse-position',
      target: el,
      coordinateFormat: (coords) => {
        if (coords) {
          this.lastCoordinates = coords;
          return format(coords, formatParam, precisionParam);
        }
        return '';
      },
    });

    this.getOpenLayersMapService().map.addControl(this.mousePosition);
  }

  add3DControl(): void {
    const { viewer } = this.getCesiumMapService();
    const { scene } = viewer;
    const entity = viewer.entities.add({
      label: {
        show: false,
        showBackground: true,
        font: '14px monospace',
        horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
        verticalOrigin: Cesium.VerticalOrigin.TOP,
        pixelOffset: new Cesium.Cartesian2(15, 0),
      },
    });

    const handler = new Cesium.ScreenSpaceEventHandler(scene.canvas);
    handler.setInputAction((movement: any) => {
      const ray = viewer.camera.getPickRay(movement.endPosition);
      const cartesian = viewer.scene.globe.pick(ray, viewer.scene);
      if (cartesian) {
        const cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(cartesian);
        const longitudeString = Cesium.Math.toDegrees(
          cartographic.longitude,
        ).toFixed(2);
        const latitudeString = Cesium.Math.toDegrees(
          cartographic.latitude,
        ).toFixed(2);
        const heightString = cartographic.height.toFixed(2);

        entity.position = cartesian;
        entity.label.show = true;
        this.posText = `Lon: ${`   ${longitudeString}`.slice(-7)}\u00B0`
          + `\nLat: ${`   ${latitudeString}`.slice(-7)}\u00B0`
          + `\nAlt: ${`   ${heightString}`.slice(-7)}m`;
      } else {
        entity.label.show = false;
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }
}
